import { differenceInCalendarDays, startOfDay } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'

import Modal from '@/components/Modal/Modal'
import { Button } from '@/components/UiElements'

import DateRangeTimeline, { TimelineFocusRequest } from './DateRangeTimeline'
import {
  getMatchingPreset,
  getPresetRange,
  OrderDatePresetId,
  OrderDateRange,
  orderDatePresets,
  parseDateKey,
  toDateKey,
} from './dateRangePresets'

interface OrderDateFilterDialogProps {
  isOpen: boolean
  value: OrderDateRange | null
  onApply: (range: OrderDateRange | null) => void
  onClose: () => void
}

const getInitialFocus = (range: OrderDateRange | null, id: number): TimelineFocusRequest => {
  if (!range) return { id, date: toDateKey(new Date()), align: 'to' }

  const from = parseDateKey(range.from)
  const to = parseDateKey(range.to)
  const rangeLength = from && to ? differenceInCalendarDays(to, from) + 1 : 1

  return rangeLength <= 30 ? { id, date: range.from, align: 'from' } : { id, date: range.to, align: 'to' }
}

const OrderDateFilterDialog = ({ isOpen, value, onApply, onClose }: OrderDateFilterDialogProps) => {
  const [draftRange, setDraftRange] = useState<OrderDateRange | null>(value)
  const [focusRequest, setFocusRequest] = useState<TimelineFocusRequest>(() => getInitialFocus(value, 0))
  const todayKey = useMemo(() => toDateKey(startOfDay(new Date())), [])
  const selectedPresetId = getMatchingPreset(draftRange)?.id

  useEffect(() => {
    if (!isOpen) return

    setDraftRange(value)
    setFocusRequest((current) => getInitialFocus(value, current.id + 1))
  }, [isOpen, value])

  const requestTimelineFocus = (date: string, align: 'from' | 'to') => {
    setFocusRequest((current) => ({ id: current.id + 1, date, align }))
  }

  const handlePresetClick = (presetId: OrderDatePresetId) => {
    const nextRange = getPresetRange(presetId)
    setDraftRange(nextRange)

    if (!nextRange) {
      requestTimelineFocus(todayKey, 'to')
      return
    }

    const from = parseDateKey(nextRange.from)
    const to = parseDateKey(nextRange.to)
    const rangeLength = from && to ? differenceInCalendarDays(to, from) + 1 : 1
    requestTimelineFocus(rangeLength <= 30 ? nextRange.from : nextRange.to, rangeLength <= 30 ? 'from' : 'to')
  }

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    if (!value) {
      setDraftRange(null)
      requestTimelineFocus(todayKey, 'to')
      return
    }

    const safeValue = value > todayKey ? todayKey : value
    let nextRange: OrderDateRange

    if (!draftRange) {
      nextRange = { from: safeValue, to: safeValue }
    } else if (field === 'from') {
      nextRange = {
        from: safeValue,
        to: safeValue > draftRange.to ? safeValue : draftRange.to,
      }
    } else {
      nextRange = {
        from: safeValue < draftRange.from ? safeValue : draftRange.from,
        to: safeValue,
      }
    }

    setDraftRange(nextRange)
    requestTimelineFocus(safeValue, field)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Filter Orders by Date"
      className="!w-[1040px] max-w-[calc(100vw-32px)] !p-0"
      containerClassName="max-md:!py-[32px]"
      closeOnOutsideClick={false}
    >
      <div className="max-h-[calc(100vh-220px)] overflow-y-auto overscroll-contain">
        <section className="p-xl">
          <div className="mb-lg grid grid-cols-[1fr_auto_1fr] items-end gap-md border-b border-gray-200 pb-lg max-sm:grid-cols-1">
            <label className="flex flex-col gap-xs">
              <span className="text-title-sm text-gray-700">From</span>
              <input
                type="date"
                max={todayKey}
                value={draftRange?.from || ''}
                onChange={(event) => handleDateChange('from', event.target.value)}
                className="h-[44px] w-full rounded-md border border-gray-300 bg-white px-md text-text-md text-gray-900 shadow-xs outline-none transition-all hover:border-primary-500 focus:border-primary-500 focus:shadow-focus focus:ring-0"
              />
            </label>
            <span className="pb-[12px] text-text-sm text-gray-400 max-sm:hidden">to</span>
            <label className="flex flex-col gap-xs">
              <span className="text-title-sm text-gray-700">To</span>
              <input
                type="date"
                max={todayKey}
                value={draftRange?.to || ''}
                onChange={(event) => handleDateChange('to', event.target.value)}
                className="h-[44px] w-full rounded-md border border-gray-300 bg-white px-md text-text-md text-gray-900 shadow-xs outline-none transition-all hover:border-primary-500 focus:border-primary-500 focus:shadow-focus focus:ring-0"
              />
            </label>
          </div>

          <div className="mb-lg flex flex-wrap gap-sm">
            {orderDatePresets.map((preset) => {
              const isSelected = selectedPresetId === preset.id

              return (
                <button
                  key={preset.id}
                  type="button"
                  className={`h-[34px] rounded-md border px-md text-title-sm transition-all ${
                    isSelected
                      ? 'border-primary-600 bg-primary-500 text-white shadow-sm text-shadow-sm'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-primary-500 hover:text-primary-700'
                  }`}
                  onClick={() => handlePresetClick(preset.id)}
                >
                  {preset.label}
                </button>
              )
            })}
          </div>

          <DateRangeTimeline range={draftRange} onChange={setDraftRange} focusRequest={focusRequest} />
        </section>

        <footer className="sticky bottom-0 flex justify-end gap-sm border-t border-gray-200 bg-white px-xl py-md">
          <Button type="alt" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onApply(draftRange)}>Apply Filter</Button>
        </footer>
      </div>
    </Modal>
  )
}

export default OrderDateFilterDialog
